// The service worker passes a failed request with this header straight back instead of queuing it
// (service-worker.js, networkSafeFetch), and strips it before the request leaves the device
export const NO_QUEUE_HEADER = 'X-Sasa-No-Queue'

// Whether this browser can run the service worker that makes the app work offline.
// `public/registerServiceWorker.js` sets `window.__serviceWorkerRegistrationFailed` when registering
// it throws: old browsers cannot run a module service worker, and some in-app browsers refuse one.
export function offlineSupportAvailable() {
  if (typeof navigator === 'undefined' || !('serviceWorker' in navigator)) return false
  return !window.__serviceWorkerRegistrationFailed
}

// Asks the service worker controlling this page about the saves it holds. Resolves with
//   queued:            the record uuids it still has requests to deliver for
//   reasons:           for the queued ones whose last attempt failed, why ('network', 'HTTP 422', ...)
//   delivered:         the ones it has delivered since the page last took them, each with when the
//                      latest delivered save was sent (0 when unknown)
//   unqueued:          the ones it gave up on and hands back for the page to send itself
//   handedBackDevices: for each of those, the device it was queued from
//   deviceUnknown:     whether the server rejected this device, which then has to be provisioned again
// or with null when that cannot be known right now: no worker controls the page, the worker is busy
// processing its queue, or it is an older one that does not answer.
export function queuedRecordState(timeoutMs = 3000) {
  const controller = navigator.serviceWorker?.controller
  if (!controller) return Promise.resolve(null)

  return new Promise((resolve) => {
    const channel = new MessageChannel()
    const timer = setTimeout(() => resolve(null), timeoutMs)

    channel.port1.onmessage = (event) => {
      clearTimeout(timer)
      if (event.data?.busy !== false) return resolve(null)

      resolve({
        queued: new Set(event.data.uuids || []),
        reasons: new Map(Object.entries(event.data.reasons || {})),
        delivered: deliveredMap(event.data.delivered),
        unqueued: new Set(event.data.unqueued || []),
        handedBackDevices: new Map(Object.entries(event.data.handedBackDevices || {})),
        deviceUnknown: Boolean(event.data.deviceUnknown)
      })
    }

    controller.postMessage({ type: 'queuedRecords' }, [channel.port2])
  })
}

// A worker from an earlier build of this change sent a plain list of uuids
function deliveredMap(delivered) {
  if (Array.isArray(delivered)) return new Map(delivered.map((uuid) => [uuid, 0]))
  return new Map(Object.entries(delivered || {}))
}

// Tells the service worker the page has taken what queuedRecordState reported -- the saves handed
// back and the ones delivered -- so it can forget them, and whether this device is now known again
export function acknowledgeQueueState({ unqueued = [], delivered = new Map(), deviceKnown = false }) {
  navigator.serviceWorker?.controller?.postMessage({
    type: 'unqueuedTaken', uuids: [...unqueued], delivered: Object.fromEntries(delivered), deviceKnown
  })
}

// Tells the service worker the page delivered these records itself, each `{ uuid, before }` with the
// time it started sending, so a copy it queued before then is dropped instead of replayed over them
export function forgetQueuedCopies(records) {
  if (typeof navigator === 'undefined') return
  navigator.serviceWorker?.controller?.postMessage({ type: 'forgetQueuedCopies', records })
}

// Asks the browser not to clear this site's storage when the phone runs low on space. Agents use the
// app in a browser tab rather than installing it, and a tab's storage is otherwise "best effort":
// the browser may delete it, and with it any work not yet sent. Chrome decides without asking the
// agent, mostly on how much the site is used, so a refusal is expected at first.
export async function requestPersistentStorage() {
  if (!navigator.storage?.persist) return false

  try {
    if (await navigator.storage.persisted()) return true

    const granted = await navigator.storage.persist()
    if (!granted) console.warn('The browser did not grant persistent storage; it may clear offline data when space runs low')
    return granted
  }
  catch (error) {
    console.warn('Could not request persistent storage', error)
    return false
  }
}

// The service worker this page can reach: the one controlling it, or else the registration's active
// one (a page loaded before the worker took control). Null when there is none.
async function reachableWorker() {
  if (typeof navigator === 'undefined' || !navigator.serviceWorker) return null
  if (navigator.serviceWorker.controller) return navigator.serviceWorker.controller

  try {
    return (await navigator.serviceWorker.getRegistration())?.active ?? null
  }
  catch {
    return null
  }
}

// Asks the service worker to end the session -- empty its queue and forget the user, key, device and
// its bookkeeping -- and waits for it to confirm. Resolves with 'cleared', 'unavailable' (no worker
// to ask) or 'unconfirmed' (no answer in time, or it failed).
export async function endWorkerSession(timeoutMs = 8000) {
  const worker = await reachableWorker()
  if (!worker) return 'unavailable'

  return new Promise((resolve) => {
    const channel = new MessageChannel()
    const timer = setTimeout(() => resolve('unconfirmed'), timeoutMs)

    channel.port1.onmessage = (event) => {
      clearTimeout(timer)
      resolve(event.data?.cleared === true ? 'cleared' : 'unconfirmed')
    }

    worker.postMessage({ type: 'endSession' }, [channel.port2])
  })
}

// The page's own way to clear what the worker keeps, when the worker could not confirm it did: its
// queue's database and its Cache Storage. A worker from before endSession existed is also asked to
// empty its queue. A database the worker still has open is deleted once the worker lets go of it,
// before the worker can read it again.
export async function clearWorkerStorage() {
  ;(await reachableWorker())?.postMessage({ type: 'clearQueue' })

  await Promise.all([
    deleteDatabase('workbox-background-sync'),
    typeof caches === 'undefined' ? null : caches.delete('appState').catch(() => false)
  ])
}

export function deleteDatabase(name) {
  if (typeof indexedDB === 'undefined') return Promise.resolve()

  return new Promise((resolve) => {
    const request = indexedDB.deleteDatabase(name)
    request.onsuccess = request.onerror = request.onblocked = () => resolve()
  })
}
